import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { FileText } from "lucide-react";

const money = (n) => `$${(n || 0).toFixed(2)}`;

export const QUOTE_STATUSES = [
  { value: "draft", label: "Draft" },
  { value: "pending_approval", label: "Pending Approval" },
  { value: "approved", label: "Approved" },
  { value: "rejected", label: "Rejected" },
];

export const quoteStatusClass = (status) =>
  ({
    approved: "bg-green-100 text-green-700 border-green-200",
    pending_approval: "bg-orange-100 text-orange-700 border-orange-200",
    rejected: "bg-red-100 text-red-700 border-red-200",
    draft: "bg-slate-100 text-slate-700 border-slate-200",
  }[status] || "bg-slate-100 text-slate-700");

const EmptyState = ({ title }) => (
  <div className="text-center py-12" data-testid="quotes-report-empty">
    <FileText className="w-16 h-16 text-slate-300 mx-auto mb-4" />
    <h3 className="text-xl font-semibold text-slate-700 mb-2">{title}</h3>
    <p className="text-slate-500">Try adjusting your filters</p>
  </div>
);

const StatCard = ({ label, value, gradient, testId }) => (
  <Card className={`bg-gradient-to-br ${gradient} text-white`}>
    <CardContent className="p-4">
      <p className="text-white/80 text-sm">{label}</p>
      <p className="text-2xl font-bold" data-testid={testId}>{value}</p>
    </CardContent>
  </Card>
);

export const QuotesSummaryReport = ({ data }) => {
  if (data.length === 0) return <EmptyState title="No quotes found" />;
  const sum = (key) => data.reduce((acc, r) => acc + (r[key] || 0), 0);
  return (
    <div className="space-y-6" data-testid="quotes-summary-report">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <StatCard label="Customers" value={data.length} gradient="from-blue-600 to-blue-700" testId="quotes-total-customers" />
        <StatCard label="Total Quoted" value={money(sum("total_quoted"))} gradient="from-indigo-600 to-indigo-700" testId="quotes-total-quoted" />
        <StatCard label="Approved" value={money(sum("total_approved"))} gradient="from-green-600 to-green-700" testId="quotes-total-approved" />
        <StatCard label="Pending Approval" value={money(sum("total_pending"))} gradient="from-orange-600 to-orange-700" testId="quotes-total-pending" />
      </div>
      <div className="space-y-4">
        {data.map((record) => (
          <Card key={record.customer_id} className="bg-slate-50" data-testid={`quotes-customer-row-${record.customer_id}`}>
            <CardContent className="p-6">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="text-lg font-bold text-slate-800">{record.customer_name}</h3>
                  {record.customer_email && <p className="text-sm text-slate-500">{record.customer_email}</p>}
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold text-indigo-600">{money(record.total_quoted)}</p>
                  <p className="text-sm text-slate-600">{record.quote_count} quotes</p>
                </div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div>
                  <p className="text-slate-500">Approved</p>
                  <p className="font-semibold text-green-600">{money(record.total_approved)} ({record.approved_count})</p>
                </div>
                <div>
                  <p className="text-slate-500">Pending Approval</p>
                  <p className="font-semibold text-orange-600">{money(record.total_pending)} ({record.pending_count})</p>
                </div>
                <div>
                  <p className="text-slate-500">Rejected</p>
                  <p className="font-semibold text-red-600">{money(record.total_rejected)} ({record.rejected_count})</p>
                </div>
                <div>
                  <p className="text-slate-500">Drafts</p>
                  <p className="font-semibold">{record.draft_count}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

export const QuotesDetailedReport = ({ quotes }) => {
  if (quotes.length === 0) return <EmptyState title="No quotes found" />;
  const total = quotes.reduce((acc, q) => acc + (q.total || 0), 0);
  const count = (status) => quotes.filter((q) => q.status === status).length;
  return (
    <div className="space-y-3" data-testid="quotes-detailed-report">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Card className="bg-blue-50"><CardContent className="p-4"><p className="text-sm text-slate-600">Total Quotes</p><p className="text-2xl font-bold text-blue-600" data-testid="quotes-detailed-count">{quotes.length}</p></CardContent></Card>
        <Card className="bg-indigo-50"><CardContent className="p-4"><p className="text-sm text-slate-600">Total Amount</p><p className="text-2xl font-bold text-indigo-600" data-testid="quotes-detailed-total">{money(total)}</p></CardContent></Card>
        <Card className="bg-green-50"><CardContent className="p-4"><p className="text-sm text-slate-600">Approved</p><p className="text-2xl font-bold text-green-600">{count("approved")}</p></CardContent></Card>
        <Card className="bg-orange-50"><CardContent className="p-4"><p className="text-sm text-slate-600">Pending Approval</p><p className="text-2xl font-bold text-orange-600">{count("pending_approval")}</p></CardContent></Card>
      </div>
      {quotes.map((quote) => (
        <Card key={quote.id} className="hover:shadow-lg transition-shadow" data-testid={`quote-row-${quote.quote_number}`}>
          <CardContent className="p-6">
            <div className="flex items-center gap-3 mb-2">
              <h3 className="text-lg font-bold text-slate-800">{quote.quote_number}</h3>
              <Badge className={quoteStatusClass(quote.status)}>{quote.status?.replace("_", " ").toUpperCase()}</Badge>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4 text-sm">
              <div><p className="text-slate-500">Customer</p><p className="font-semibold text-slate-800">{quote.customer_name}</p></div>
              <div><p className="text-slate-500">Created By</p><p className="font-semibold">{quote.created_by}</p></div>
              <div><p className="text-slate-500">Date</p><p className="font-semibold">{String(quote.created_at || "").split("T")[0]}</p></div>
              <div><p className="text-slate-500">Valid Until</p><p className="font-semibold">{quote.valid_until}</p></div>
              <div><p className="text-slate-500">Amount</p><p className="text-2xl font-bold text-indigo-600">{money(quote.total)}</p></div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};
